import { Box, Card, Flex, Text } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { AutoIcon } from 'components/common/custom-icon/shorthands';
import { DeliveryControl } from 'components/common/delivery-control';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { VideoDeliveryBadge } from 'components/common/video/delivery-badge';
import { CommonVideoPlayerHoC } from 'components/common/video/player';
import { VideoPreviewButton } from 'components/common/video/preview-button';
import { PitchListSidebarSectionTitle } from 'components/sections/pitch-list/sidebar/section-title';
import { VideoPlayerType } from 'enums/video-player.enums';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { VideoChangedFn } from 'interfaces/function-types';
import { IVerboseComponent } from 'interfaces/i-components';
import {
  IDeliveryProps,
  IVideoOption,
  IVideoSuggestionsRequest,
} from 'interfaces/i-video-options';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { AUTO_USE_TOP_SUGGESTION } from 'lib_ts/enums/videos.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IVideoPlayback } from 'lib_ts/interfaces/i-video';
import { first } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import { useVideosStore } from 'stores/use-videos-store';

const MAX_SUGGESTIONS = 5;
const LIST_MIN_WIDTH = '200px';
const LIST_MAX_WIDTH = '400px';

interface IProps extends IDeliveryProps {
  previewPx?: number;
  previewPz?: number;

  headerAsInput?: boolean;
  hideHeader?: boolean;
  videoID?: Nullable<string>;

  disabled?: boolean;

  selectConfig?: {
    releasePx: number;
    onChange: VideoChangedFn;
  };

  hidePlayback?: boolean;
  hidePreviewButton?: boolean;
  hideDeliveryControl?: boolean;

  // for PL sidebar to recommend based on pitchBP + active hitter
  suggestionsRequest?: IVideoSuggestionsRequest;
}

// for previewing pitcher videos
export const CommonVideoPreview = (props: IProps & IVerboseComponent) => {
  const current = useAuthStore((s) => s.current);

  const loadingVideos = useVideosStore((s) => s.loading);

  // Show recently uploaded first if the user is not searching for a video
  const [searching, setSearching] = useState(false);

  const [defaultOptions, setDefaultOptions] = useState<IVideoOption[]>([]);
  const [suggestedOptions, setSuggestedOptions] = useState<IVideoOption[]>([]);

  // update options available to the input
  useEffect(() => {
    const result = useVideosStore.getState().getVideoOptions({
      currentID: props.videoID,
      releasePx: props.selectConfig?.releasePx ?? 0,
      recentlyUploadedFirst: !searching, // don't prioritize recently uploaded while searching
      suggestions: props.suggestionsRequest
        ? {
            ...props.suggestionsRequest,
            delivery: props.delivery,
          }
        : undefined,
    });

    verboseDebug(
      props,
      `got video options > found ${result.suggestions.length} suggestions`
    );

    setDefaultOptions(result.options);
    setSuggestedOptions(result.suggestions.slice(0, MAX_SUGGESTIONS));
  }, [
    props.videoID,
    props.selectConfig?.releasePx,
    props.suggestionsRequest,
    searching,
    props.delivery,
  ]);

  const options = useMemo(
    () =>
      [...suggestedOptions, ...defaultOptions].map((o) => ({
        ...o,
        // ? options in store don't have suffix as a ReactNode
        suffix: <VideoDeliveryBadge value={o.deliveryType} />,
      })),
    [suggestedOptions, defaultOptions]
  );

  const [playback, setPlayback] = useState<IVideoPlayback>();

  const assignedSuggestion = useMemo(() => {
    if (!AUTO_USE_TOP_SUGGESTION) {
      // don't use suggestions if disabled site-wide
      return undefined;
    }

    if (current.disable_auto_videos) {
      // don't use suggestions if disabled for user
      return undefined;
    }

    if (props.videoID) {
      // don't use suggestions if there is a selected video
      return undefined;
    }

    return first(suggestedOptions);
  }, [current.disable_auto_videos, props.videoID, suggestedOptions]);

  // selected (by props.videoID) if any, otherwise assigned suggestion if any
  const activeVideo = useMemo(() => {
    if (props.videoID) {
      return options.find((o) => o.value === props.videoID)?.video;
    }

    if (assignedSuggestion) {
      return assignedSuggestion.video;
    }

    return undefined;
  }, [props.videoID, options, assignedSuggestion]);

  const latestActiveVideoID = useLatest(activeVideo?._id);

  // auto-load playback for activeVideo
  useEffect(() => {
    if (!activeVideo?._id) {
      // clear playback so that things w/o videos render the "no video selected" visual
      setPlayback(undefined);
      return;
    }

    useVideosStore
      .getState()
      .lazyLoadPlayback(activeVideo._id)
      .then((result) => {
        if (latestActiveVideoID.current !== result?.video._id) {
          verboseDebug(
            props,
            `setting playback > pitch video > skipped mismatch`,
            result
          );
          return;
        }

        verboseDebug(props, 'setting playback > pitch video', result);
        setPlayback(result);
      })
      .catch((e) => {
        console.error(e);
        setPlayback(undefined);
      });
  }, [activeVideo, setPlayback]);

  // force re-render of specific components whenever playback loads
  const playbackKey = useMemo(() => Date.now(), [playback]);

  const subLabel = useMemo(() => {
    if (props.selectConfig) {
      return undefined;
    }

    if (!playback) {
      return t('videos.no-video');
    }

    const { video } = playback;
    const { VideoTitle, VideoFileName } = video;

    return VideoTitle || VideoFileName || t('videos.untitled');
  }, [props.selectConfig, playback]);

  const loadingOrDisabled = props.disabled || loadingVideos;

  return (
    <ErrorBoundary identifier="CommonVideoPreview">
      <Flex direction="column" gap="2">
        {!props.hideHeader && (
          <Flex align="center">
            <Box flexGrow="1">
              {props.headerAsInput ? (
                <CommonInputWrapper id="video-preview">
                  <CommonInputLabel label="videos.video-preview" />
                </CommonInputWrapper>
              ) : (
                <PitchListSidebarSectionTitle
                  label="videos.video-preview"
                  subLabel={subLabel}
                />
              )}
            </Box>

            {!props.hidePreviewButton && (
              <VideoPreviewButton
                videoID={activeVideo?._id ?? null}
                px={props.previewPx}
                pz={props.previewPz}
                disabled={loadingOrDisabled}
              />
            )}
          </Flex>
        )}

        {!props.hideDeliveryControl && (
          <DeliveryControl
            disabled={props.disabled}
            onDisabled={props.onDisabled}
            delivery={props.delivery}
            onChangeDelivery={props.onChangeDelivery}
          />
        )}

        {props.selectConfig && (
          <CommonSearchInput
            id="video-preview-videoID"
            name="video_id"
            values={props.videoID ? [props.videoID] : []}
            options={options}
            disabled={loadingOrDisabled}
            onChange={(videoIDs) =>
              props.selectConfig?.onChange(first(videoIDs) ?? null)
            }
            onSearch={
              (q) => setSearching(q.trim().length > 0) // prevent empty string from locking us into searching state
            }
            contentMinWidth={LIST_MIN_WIDTH}
            contentMaxWidth={LIST_MAX_WIDTH}
            displayValue={assignedSuggestion?.label}
            rightSlot={
              assignedSuggestion ? (
                <Text
                  title="Suggested video"
                  style={{
                    // allows the icon to display centered vertically
                    lineHeight: '0px',
                  }}
                >
                  <AutoIcon />
                </Text>
              ) : undefined
            }
            optional
            skipSort
          />
        )}

        {!props.hidePlayback && !playback && (
          <Card
            key={`placeholder-${playbackKey}`}
            style={{
              aspectRatio: '16 / 10',
              textAlign: 'center',
            }}
          >
            <Flex height="100%" align="center" justify="center">
              <Text color={RADIX.COLOR.NEUTRAL}>
                {t('videos.no-video-selected')}
              </Text>
            </Flex>
          </Card>
        )}

        {!props.hidePlayback && playback && (
          <Box>
            <CommonVideoPlayerHoC
              key={`player-${playbackKey}`}
              playback={playback}
              type={VideoPlayerType.Pitcher}
            />
          </Box>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
